import React from 'react';
import {navigate} from '@reach/router';
import {DateTime} from 'luxon';
import classNames from 'classnames';
import {Form, Formik, Field} from 'formik';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';

import {Alert, Button, FormFeedback, FormGroup, Input, Label, CustomInput} from 'reactstrap';
import {useUserDispatch} from 'contexts/user';
import {usePhaseState} from 'funnel-schedule/phase-context';
import {submitForm} from 'utils/forms';
import track, {identify} from 'utils/track';
import './OptinForm.scss';

const {OPTIN_FORM_ID} = process.env;
const {SITE_ID} = process.env;

const customInputForm = ({field, form: {touched, errors}, ...props}) => (
  <>
    <InputMask {...field} {...props}>
      {(inputProps) => (
        <Input {...inputProps} invalid={!!(touched[field.name] && errors[field.name])} onChange={field.onChange} />
      )}
    </InputMask>
    {touched[field.name] && errors[field.name] && <FormFeedback>{errors[field.name]}</FormFeedback>}
  </>
);

const customCheckboxForm = ({field, form: {touched, errors}, ...props}) => (
  <>
    <CustomInput invalid={!!(touched[field.name] && errors[field.name])} {...field} {...props} />
    {touched[field.name] && errors[field.name] && <FormFeedback>{errors[field.name]}</FormFeedback>}
  </>
);

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  smsNumber: Yup.string().when('bulk_sms', {
    is: true,
    then: (s) =>
      s
        .matches(/(^\+1 \([0-9]{3}\) [0-9]{3}-[0-9]{4}$)/, 'Please enter a valid US telephone number')
        .required('Required')
  })
});

export default function OptinForm({id, isEvergreen = false, onComplete = () => {}, ...props}) {
  const userDispatch = useUserDispatch();

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: ''
      }
    }
  } = usePhaseState();

  const toOrdinalSuffix = (num) => {
    const int = parseInt(num),
      digits = [int % 10, int % 100],
      ordinals = ['st', 'nd', 'rd', 'th'],
      oPattern = [1, 2, 3, 4],
      tPattern = [11, 12, 13, 14, 15, 16, 17, 18, 19];
    return oPattern.includes(digits[0]) && !tPattern.includes(digits[1])
      ? int + ordinals[digits[0] - 1]
      : int + ordinals[3];
  };

  const launchStart = DateTime.fromISO(defaultSchedule.launchStart);
  const launchStartNum = launchStart.setZone('America/Los_Angeles').toFormat('d');
  const launchEnd = DateTime.fromISO(defaultSchedule.launchEnd);
  const launchEndNum = launchStart.setZone('America/Los_Angeles').toFormat('d');

  return (
    <>
      <p className="text-center">
        Enter your name and email below to <br className="d-none d-sm-block d-xl-none" />
        <strong>watch the full film immediately.</strong>
      </p>
      <div className="optin-wrap mb-3">
        <Formik
          initialValues={{
            email: '',
            firstName: '',
            bulk_sms: false,
            smsNumber: ''
          }}
          validationSchema={SignupSchema}
          onSubmit={(values, actions) => {
            return submitForm(values, OPTIN_FORM_ID)
              .then(({data}) => {
                identify(data.proxy_data.user.uuid, {
                  id: data.proxy_data.user.uuid,
                  firstName: values.firstName,
                  email: values.email,
                  ...(values.smsNumber ? {phone: values.smsNumber} : {})
                });
                track('User Registered', {
                  property: SITE_ID,
                  entryPoint: 'Landing',
                  label: `${SITE_ID} - Film`
                });
                return userDispatch({
                  type: 'SET_USER',
                  payload: {
                    firstName: values.firstName,
                    email: values.email,
                    registrationId: data.proxy_data.registrationId,
                    mcFormReqUUID: data.proxy_data.mc_form_uuid,
                    uuid: data.proxy_data.user.uuid
                  }
                });
              })
              .then(() => onComplete())
              .then(() => navigate('/screening/'))
              .catch((e) => {
                actions.setStatus({error: e});
                actions.setSubmitting(false);
              });
          }}
          render={({values, handleSubmit, isSubmitting, isValid, handleReset, status, ...props}) => (
            <Form>
              {status && status.error && <Alert color="danger">{status.error}</Alert>}
              <FormGroup>
                <Label for="firstName" hidden>
                  First Name
                </Label>
                <Field
                  name="firstName"
                  type={'text'}
                  component={customInputForm}
                  placeholder="What is your first name?"
                />
              </FormGroup>
              <FormGroup>
                <Label for="email" hidden>
                  Email
                </Label>
                <Field name="email" type={'email'} component={customInputForm} placeholder="What is your best email?" />
              </FormGroup>

              <Button
                className="text-uppercase text-h4 text-condensed text-800"
                color="cta"
                type="submit"
                disabled={isSubmitting || !isValid}
                block
              >
                {isSubmitting ? 'Loading...' : 'Watch For Free Now'}
              </Button>
            </Form>
          )}
        />
      </div>
      <p className="text-small text-left mb-0">
        By entering your information here, you are agreeing to receive messages from Food Revolution Network and Earth
        Conscious Life. Your email address will NEVER be shared or sold. You are always free to easily unsubscribe at
        any time. See{' '}
        <a
          className={classNames(`text-underline link-${props.linkColor}`)}
          href="https://foodrevolution.org/privacy-policy/"
          target="_blank"
        >
          FRN’s Privacy Policy here
        </a>{' '}
        and{' '}
        <a
          className={classNames(`text-underline link-${props.linkColor}`)}
          href="https://www.earthconsciouslife.org/privacy-policy"
          target="_blank"
        >
          Earth Conscious Life Privacy Policy here
        </a>{' '}
        for more information.
      </p>
    </>
  );
}
